import { memo, useCallback } from 'react';
import type { ConnectorSide } from '../types';
import { NODE_WIDTH, NODE_HEIGHT, CONNECTOR_RADIUS } from '../types';
import { useCanvasConnectorOps } from '../hooks/useCanvasConnectorOps';

/** Gradient direction matches connector side — inner (toward node) first, outer (away) second */
function getSplitGradient(side: ConnectorSide, innerColor: string, outerColor: string): string {
  return `linear-gradient(to ${side}, ${innerColor} 50%, ${outerColor} 50%)`;
}

function getConnectorCssPosition(side: ConnectorSide, width: number, height: number): React.CSSProperties {
  const R = CONNECTOR_RADIUS;
  switch (side) {
    case 'top':    return { left: width / 2 - R, top: -R };
    case 'right':  return { right: -R, top: height / 2 - R };
    case 'bottom': return { left: width / 2 - R, bottom: -R };
    case 'left':   return { left: -R, top: height / 2 - R };
  }
}

export interface CanvasConnectorProps {
  taskId: string;
  side: ConnectorSide;
  isDropTarget: boolean;
  dropTargetSide: ConnectorSide | null;
  isEdgeEndpoint: boolean;
  hasEdge: boolean;
  entityWidth?: number;
  entityHeight?: number;
}

const D = CONNECTOR_RADIUS * 2;

function CanvasConnectorInner({
  taskId,
  side,
  isDropTarget,
  dropTargetSide,
  isEdgeEndpoint,
  hasEdge,
  entityWidth = NODE_WIDTH,
  entityHeight = NODE_HEIGHT,
}: CanvasConnectorProps) {
  const { handleConnectorMouseDown, handleConnectorRedock } = useCanvasConnectorOps();

  const pos = getConnectorCssPosition(side, entityWidth, entityHeight);
  const isThisDropTarget = isDropTarget && dropTargetSide === side;

  const handleSimplePointerDown = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
    handleConnectorMouseDown(taskId, side, e);
  }, [taskId, side, handleConnectorMouseDown]);

  const handleEdgePointerDown = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
    // Detect inner vs outer half via screen-space geometry (rotation-safe)
    const connRect = (e.target as HTMLElement).getBoundingClientRect();
    const connCx = connRect.left + connRect.width / 2;
    const connCy = connRect.top + connRect.height / 2;
    const parentEl = (e.target as HTMLElement).closest('[data-canvas-entity]');
    if (parentEl) {
      const parentRect = parentEl.getBoundingClientRect();
      const parentCx = parentRect.left + parentRect.width / 2;
      const parentCy = parentRect.top + parentRect.height / 2;
      const inDx = parentCx - connCx;
      const inDy = parentCy - connCy;
      const clickDx = e.clientX - connCx;
      const clickDy = e.clientY - connCy;
      const dot = clickDx * inDx + clickDy * inDy;
      if (dot > 0) {
        handleConnectorRedock(taskId, side, e);
        return;
      }
    }
    handleConnectorMouseDown(taskId, side, e);
  }, [taskId, side, handleConnectorMouseDown, handleConnectorRedock]);

  // z-index must be above resize handles (z-index: 10)
  const connectorZ = 20;

  // Drop target: solid green, no split
  if (isThisDropTarget) {
    return (
      <div
        data-connector={side}
        data-capture-hide
        className="absolute rounded-full border-2 border-green-500 bg-green-50 scale-125 cursor-crosshair transition-colors"
        style={{ width: D, height: D, zIndex: connectorZ, pointerEvents: 'auto', ...pos }}
        onPointerDown={handleSimplePointerDown}
      />
    );
  }

  // Connector with edge: gradient split + inner/outer hit detection
  if (hasEdge) {
    const innerColor = isEdgeEndpoint ? '#dbeafe' : '#e5e7eb'; // blue-100 : gray-200
    const outerColor = '#ffffff';
    const borderColor = isEdgeEndpoint ? '#60a5fa' : '#9ca3af'; // blue-400 : gray-400

    return (
      <div
        data-connector={side}
        data-capture-hide
        className="absolute rounded-full transition-colors cursor-crosshair"
        style={{
          width: D,
          height: D,
          zIndex: connectorZ,
          ...pos,
          border: `2px solid ${borderColor}`,
          background: getSplitGradient(side, innerColor, outerColor),
          pointerEvents: 'auto',
        }}
        onPointerDown={handleEdgePointerDown}
      />
    );
  }

  // Simple connector (no edge)
  return (
    <div
      data-connector={side}
      data-capture-hide
      className={`absolute rounded-full transition-colors cursor-crosshair ${
        isEdgeEndpoint
          ? 'border-2 border-blue-500 bg-blue-50'
          : 'border-2 border-gray-300 bg-white'
      }`}
      style={{ width: D, height: D, zIndex: connectorZ, pointerEvents: 'auto', ...pos }}
      onPointerDown={handleSimplePointerDown}
    />
  );
}

export const CanvasConnector = memo(CanvasConnectorInner);
